import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useState, useMemo } from 'react';
import {
  DataGridPremium,
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  type GridListViewColDef,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { createExcelExportWorker } from '@/utils/excelExportWorker';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { useLocalization } from '@/hooks/useLocalization';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import {
  useGetHoldingHistoricalTrendQuery,
  HoldingHistoricalTrendQueryParams,
} from '@/store/holdingsApi';
import { formatNumberToCurrency } from '@/utils/currency';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import { Capacitor } from '@capacitor/core';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { formatAppDate } from '@/utils/date';
import { formatPercentValue } from '@/utils/numbers';
import {
  GridContainer,
  type GridContainerSize,
} from '@/components/grids/GridContainer';

export interface SingleHistoricalTrendProps {
  holdingId: number;
  startDate: string;
  endDate: string;
  asOfDate?: string;
  isWidget?: boolean;
  investorId?: number;
  size?: GridContainerSize;
}

type SingleHistoricalRow = {
  id: number;
  name: string;
  startValue: number;
  endValue: number;
  dollarChange: number;
  percentChange: number;
  startPointDate?: string;
  endPointDate?: string;
};

// Match HistoricalGrid styling
const gridStyles = {
  fontSize: '14px',
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: (theme: Theme) =>
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : theme.palette.grey[100],
    borderBottom: '1px solid rgba(224, 224, 224, 0.2)',
  },
  '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
    fontSize: '14px',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(224, 224, 224, 0.2)',
    py: 1,
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-row:last-child .MuiDataGrid-cell': {
    borderBottom: 'none',
  },
};

// Match HistoricalGrid value color logic
const getValueColor = (value: number) => {
  if (value > 0) return 'success.main';
  if (value < 0) return 'error.main';
  return 'text.primary';
};

// Match HistoricalGrid column creation patterns
const createBaseColumnProps = () => ({
  headerAlign: 'right' as const,
  align: 'right' as const,
});

const SingleHistoricalTrend = ({
  holdingId,
  startDate,
  endDate,
  asOfDate,
  isWidget = false,
  investorId,
  size,
}: SingleHistoricalTrendProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const authLoaded = useAuthIsLoaded();
  const apiRef = useGridApiRef();
  const { t } = useLocalization();
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const params: HoldingHistoricalTrendQueryParams | null = useMemo(() => {
    if (!holdingId || !startDate || !endDate) return null;
    return { holdingId, startDate, endDate, asOfDate, investorId };
  }, [holdingId, startDate, endDate, asOfDate, investorId]);

  const { data, isLoading, isFetching, isError } =
    useGetHoldingHistoricalTrendQuery(
      authLoaded && params ? params : skipToken,
    );

  const row: SingleHistoricalRow | null = useMemo(() => {
    if (!data?.Records || data.Records.length === 0) return null;
    const holding = data.Records[0];
    if (holding.data.length === 0) return null;
    const firstDataPoint = holding.data[0];
    const lastDataPoint = holding.data[holding.data.length - 1];
    const startValue = firstDataPoint.Value || 0;
    const endValue = lastDataPoint.Value || 0;
    const dollarChange = endValue - startValue;
    const percentChange =
      startValue !== 0 ? (dollarChange / startValue) * 100 : 0;
    return {
      id: holding.id,
      // HoldingsTrendItem types name as always-present, but the trend API
      // can return a malformed record with a null name (see the same
      // guard in holdingsHistoricalTable.tsx). typeof-check instead of `??`
      // since the type disagrees with the runtime shape.
      name: typeof holding.name === 'string' ? holding.name : '',
      startValue,
      endValue,
      dollarChange,
      percentChange,
      startPointDate: firstDataPoint.Date,
      endPointDate: lastDataPoint.Date,
    };
  }, [data]);

  const startLabel = `${formatAppDate(row?.startPointDate ?? startDate)} ${t(
    'grid.balance',
    'Value',
  )}`;
  const endLabel = `${formatAppDate(row?.endPointDate ?? endDate)} ${t(
    'grid.balance',
    'Value',
  )}`;

  const listViewColDef: GridListViewColDef<SingleHistoricalRow> = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: (params: GridRenderCellParams<SingleHistoricalRow>) => {
        const { row: r, api } = params;

        const visibilityModel = api.state.columns.columnVisibilityModel;
        const isFieldVisible = (field: string) =>
          (visibilityModel[field] as boolean | undefined) !== false;

        const name = r.name.trim();
        const sv = r.startValue;
        const ev = r.endValue;
        const dc = r.dollarChange;
        const pc = r.percentChange;

        const Line = (line: {
          left: string;
          right: string;
          rightSx?: object;
        }) => (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography
              variant='body2'
              sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }}
            >
              {line.left}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                whiteSpace: 'nowrap',
                textAlign: 'right',
                color: 'text.secondary',
                ...line.rightSx,
              }}
            >
              {line.right}
            </Typography>
          </Box>
        );

        return (
          <Box
            sx={{
              width: '100%',
              px: 0,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {isFieldVisible('name') && (
              <Typography
                variant='body1'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                  fontWeight: '600 !important',
                }}
              >
                {name}
              </Typography>
            )}

            {isFieldVisible('startValue') && (
              <Line
                left={startLabel}
                right={formatNumberToCurrency(sv)}
              />
            )}

            {isFieldVisible('endValue') && (
              <Line
                left={endLabel}
                right={formatNumberToCurrency(ev)}
              />
            )}

            {isFieldVisible('dollarChange') && (
              <Line
                left={t('grid.dollarChange', '$ Change')}
                right={formatNumberToCurrency(dc)}
                rightSx={{
                  color: getValueColor(dc),
                  fontWeight: dc !== 0 ? '600 !important' : undefined,
                }}
              />
            )}

            {isFieldVisible('percentChange') && (
              <Line
                left={t('grid.percentChange', '% Change')}
                right={`${pc.toFixed(2)}%`}
                rightSx={{
                  color: getValueColor(pc),
                  fontWeight: pc !== 0 ? '600 !important' : undefined,
                }}
              />
            )}
          </Box>
        );
      },
    }),
    [startLabel, endLabel, t],
  );

  if (isError) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Typography color='error'>
          {t('errorLoadingHistoricalData', 'Error loading historical data.')}
        </Typography>
      </Box>
    );
  }

  if (!row && !isLoading && !isFetching && authLoaded) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Typography>
          {t('noHistoricalDataAvailable', 'No historical data available.')}
        </Typography>
      </Box>
    );
  }

  const columns: GridColDef<SingleHistoricalRow>[] = [
    {
      field: 'name',
      headerName: t('name'),
      flex: 2,
      valueFormatter: (value: string | undefined) => {
        return value || '';
      },
    },
    {
      ...createBaseColumnProps(),
      field: 'startValue',
      headerName: startLabel,
      type: 'number',
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Typography sx={{ color: getValueColor(Number(params.value)) }}>
            {formatNumberToCurrency(Number(params.value))}
          </Typography>
        </Box>
      ),
    },
    {
      ...createBaseColumnProps(),
      field: 'endValue',
      headerName: endLabel,
      type: 'number',
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Typography sx={{ color: getValueColor(Number(params.value)) }}>
            {formatNumberToCurrency(Number(params.value))}
          </Typography>
        </Box>
      ),
    },
    {
      ...createBaseColumnProps(),
      field: 'dollarChange',
      headerName: t('grid.dollarChange', '$ Change'),
      type: 'number',
      flex: 1,
      renderCell: (params) => {
        const value = Number(params.value);
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography sx={{ color: getValueColor(value) }}>
              {formatNumberToCurrency(value)}
            </Typography>
          </Box>
        );
      },
    },
    {
      ...createBaseColumnProps(),
      field: 'percentChange',
      headerName: t('grid.percentChange', '$ Change'),
      type: 'number',
      flex: 1,
      renderCell: (params) => {
        const value = Number(params.value);
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography sx={{ color: getValueColor(value) }}>
              {formatPercentValue(value)}
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        px: 1,
        mt: 3,
        '& .MuiDataGrid-cell': {
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiDataGrid-cell--textLeft': {
          justifyContent: 'flex-start',
        },
        '& .MuiDataGrid-cell--textRight': {
          justifyContent: 'flex-end',
        },
        ...(isXs && {
          '& .MuiDataGrid-main': { width: '100%' },
          '& .MuiDataGrid-virtualScroller': { width: '100%' },
          '& .MuiDataGrid-virtualScrollerRenderZone': {
            width: '100% !important',
            minWidth: '100% !important',
            maxWidth: '100% !important',
          },
          '& .MuiDataGrid-virtualScrollerContent': { width: '100% !important' },
          '& .MuiDataGrid-virtualScrollerContent > div': {
            width: '100% !important',
            maxWidth: '100% !important',
          },
          '& .MuiDataGrid-row': {
            display: 'flex',
            flexWrap: 'nowrap',
            width: '100% !important',
            minWidth: '100% !important',
            maxWidth: '100% !important',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            flexWrap: 'nowrap',
            width: '100% !important',
            minWidth: '100% !important',
            maxWidth: '100% !important',
            flex: '1 1 0',
          },
          '& .MuiDataGrid-cellContent': {
            width: '100%',
            flex: 1,
            minWidth: 0,
          },
        }),
      }}
    >
      <GridContainer
        isWidget={isWidget}
        size={size}
      >
        <DataGridPremium
          key={isXs ? 'single-historical-xs' : 'single-historical'}
          rows={row ? [row] : []}
          columns={columns}
          apiRef={apiRef}
          disableRowSelectionOnClick
          showToolbar
          disablePivoting
          listView={isXs}
          {...(isXs ? { listViewColumn: listViewColDef } : {})}
          {...(isXs ? { getRowHeight: () => 'auto' as const } : {})}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          slots={{
            columnMenu: () => null,
            ...(isNative
              ? {
                  toolbar: () => (
                    <CustomToolbarForNative
                      excelWorker={createExcelExportWorker}
                    />
                  ),
                }
              : isXs
                ? { toolbar: XsPreferencesDrawerToolbar }
                : {}),
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'skeleton',
            },
            columnsManagement: {
              getTogglableColumns: (cols: Array<{ field: string }>) =>
                cols
                  .map((c) => c.field)
                  .filter(
                    (field) =>
                      field !== 'actions' &&
                      field !== 'listColumn' &&
                      field !== '__list__' &&
                      !field.startsWith('__'),
                  ),
            },
            columnsPanel: {
              getTogglableColumns: (cols: Array<{ field: string }>) =>
                cols
                  .map((c) => ({ field: c.field }))
                  .filter(
                    (c) =>
                      c.field !== 'actions' &&
                      c.field !== 'listColumn' &&
                      c.field !== '__list__' &&
                      !c.field.startsWith('__'),
                  ),
            },
            ...(isXs
              ? { toolbar: { showQuickFilter: true } }
              : {
                  toolbar: {
                    ...defaultToolbarExportSlotProps,
                    showQuickFilter: true,
                    excelOptions: {
                      ...defaultToolbarExportSlotProps.excelOptions,
                      worker: createExcelExportWorker,
                    },
                  },
                }),
          }}
          disableColumnMenu
          loading={isLoading || isFetching || !authLoaded}
          sx={gridStyles}
        />
      </GridContainer>
    </Box>
  );
};

export default SingleHistoricalTrend;
